import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOverlayContext, useStateInRef } from 'seagull';
import { routes } from '~/constants';
import { useIoContext } from '~/contexts';
import { useDuplicateRelationship } from '~/hooks/useDuplicateRelationship';
import { useScheduledProposalActions } from '~/hooks/useScheduledProposal';
import { useTerminateAgreement } from '~/hooks/useTerminateAgreement';
import { proposalStageAnchor } from '~/models/service/stageScheduleContext';
import { draftUpdateModal, saveDraftWithLoader } from './useEditDraftsTableRowActions.utils';
import { useUpdateDraft } from './useUpdateDraft';
export const useEditDraftsTableRowActions = (param)=>{
    let { setDrafts } = param;
    const { getNow } = useIoContext();
    const navigate = useNavigate();
    const { duplicateDraftFromId } = useDuplicateRelationship();
    const { terminateDraftById } = useTerminateAgreement();
    const { showModal, startLoader, stopLoader } = useOverlayContext();
    const { updateDraft: saveDraft } = useUpdateDraft();
    const { showSwitchToEditModal } = useScheduledProposalActions();
    const updateDraft = useCallback((updatedDraft)=>{
        const withLastModifiedOn = updatedDraft.setLastModifiedOn(getNow().getTime());
        setDrafts((drafts)=>drafts.map((d)=>d.id() === updatedDraft.id() ? withLastModifiedOn : d));
    }, [
        setDrafts
    ]);
    const deleteDraft = useCallback((draft)=>setDrafts((drafts)=>drafts.filter((d)=>d.id() !== draft.id())), [
        setDrafts
    ]);
    const stageAnchorOf = (draft)=>proposalStageAnchor({
            effectiveDate: draft.effectiveDateAsDate(),
            now: getNow()
        });
    const getActionDeps = useStateInRef({
        navigate,
        duplicateDraftFromId,
        terminateDraftById,
        showModal,
        startLoader,
        stopLoader,
        saveDraft,
        showSwitchToEditModal,
        updateDraft,
        deleteDraft
    });
    const editDraft = useCallback((draft)=>{
        const deps = getActionDeps();
        if (!draft.isScheduled()) return deps.navigate(routes.draft(draft.id()));
        deps.showSwitchToEditModal({
            draftId: draft.id(),
            isSampleRelationship: draft.isSample(),
            refresh: ()=>{}
        });
    }, []);
    const duplicateDraft = useCallback((draftId)=>{
        getActionDeps().duplicateDraftFromId(draftId);
    }, []);
    const discardDraft = useCallback((draft)=>{
        const deps = getActionDeps();
        deps.terminateDraftById(draft.id(), ()=>deps.deleteDraft(draft));
    }, []);
    const openDraftUpdateModal = useCallback((draft, options)=>{
        const deps = getActionDeps();
        deps.showModal(draftUpdateModal({
            draft,
            options,
            changeDraftInRow: deps.updateDraft
        }));
    }, []);
    const addServices = useCallback((draft, services, packageId)=>{
        const deps = getActionDeps();
        const newDraft = draft.addServices(services, packageId, stageAnchorOf(draft));
        deps.saveDraft(saveDraftWithLoader({
            newDraft,
            deps
        }));
    }, []);
    const rowActions = useMemo(()=>({
            editDraft,
            duplicateDraft,
            discardDraft,
            openDraftUpdateModal,
            addServices
        }), [
        editDraft,
        duplicateDraft,
        discardDraft,
        openDraftUpdateModal,
        addServices
    ]);
    return {
        rowActions
    };
};
